import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  TablePagination,
} from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import SearchIcon from "@mui/icons-material/Search";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const Patients = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [inactive, setInactive] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Exact Design Palette from Screenshot
  const tealMain = "#1E5E5F"; // Form outline border & Top ADD button
  const tealButton = "#245E65"; // Search & Clear buttons
  const importGreen = "#057A44"; // Import button
  const labelTeal = "#276A6C"; // Input label teal color
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";

  // Extended Comprehensive Patient Records for Scrolling
  const [patientList, setPatientList] = useState([
    { id: "1045", firstName: "Gabriel", lastName: "Vance", dob: "1987-03-12", illness: "Chronic Migraine", status: "Active", intake: "09/14/2026", phone: "+1 (415) 555-0142", email: "g.vance@neurohealth.com" },
    { id: "1044", firstName: "Zoe", lastName: "Kensington", dob: "1992-06-19", illness: "Post-Concussion Syndrome", status: "In Treatment", intake: "09/12/2026", phone: "+1 (212) 555-0198", email: "zoe.k@careportal.net" },
    { id: "1043", firstName: "Julian", lastName: "Mercer", dob: "1980-11-04", illness: "Parkinson's Evaluation", status: "Active", intake: "09/10/2026", phone: "+1 (617) 555-0123", email: "j.mercer@medics.org" },
    { id: "1042", firstName: "Maya", lastName: "Lin", dob: "1995-01-28", illness: "Epilepsy Monitoring", status: "Active", intake: "09/08/2026", phone: "+1 (206) 555-0176", email: "maya.lin@seattlemed.edu" },
    { id: "1041", firstName: "Caleb", lastName: "Thornton", dob: "1983-09-15", illness: "Neuropathy Screening", status: "Pending", intake: "09/05/2026", phone: "+1 (312) 555-0155", email: "c.thornton@windycity.org" },
    { id: "1040", firstName: "Stella", lastName: "Abernathy", dob: "1990-12-01", illness: "Vestibular Disorder", status: "Active", intake: "09/03/2026", phone: "+1 (512) 555-0189", email: "stella.a@austincare.io" },
    { id: "1039", firstName: "Nathaniel", lastName: "Cross", dob: "1978-05-22", illness: "Cervical Spondylosis", status: "In Treatment", intake: "09/02/2026", phone: "+1 (305) 555-0111", email: "n.cross@miamihealth.net" },
    { id: "1038", firstName: "Penelope", lastName: "Vaughn", dob: "1996-08-14", illness: "Sleep Apnea Assessment", status: "Active", intake: "08/30/2026", phone: "+1 (646) 555-0134", email: "p.vaughn@manhattanmed.org" },
    { id: "1037", firstName: "Declan", lastName: "O'Reilly", dob: "1989-02-09", illness: "Traumatic Brain Injury", status: "In Treatment", intake: "08/27/2026", phone: "+353 1 555 0192", email: "declan.oreilly@dublinmed.ie" },
    { id: "1036", firstName: "Freya", lastName: "Lindqvist", dob: "1993-10-31", illness: "Cluster Headaches", status: "Active", intake: "08/25/2026", phone: "+46 8 555 0145", email: "freya.l@stockholmcare.se" },
    { id: "1035", firstName: "Marcus", lastName: "Aurelius", dob: "1982-07-07", illness: "Spinal Stenosis", status: "Active", intake: "08/22/2026", phone: "+1 (404) 555-0167", email: "m.aurelius@peachhealth.com" },
    { id: "1034", firstName: "Sienna", lastName: "Miller", dob: "1991-04-16", illness: "Essential Tremor", status: "Pending", intake: "08/20/2026", phone: "+1 (702) 555-0158", email: "sienna.m@nevadacare.net" },
    { id: "1033", firstName: "Jonah", lastName: "Hex", dob: "1986-09-29", illness: "Radiculopathy Check", status: "Active", intake: "08/18/2026", phone: "+1 (858) 555-0199", email: "j.hex@sandiegomed.org" },
    { id: "1032", firstName: "Iris", lastName: "West", dob: "1994-03-05", illness: "Neuromuscular Review", status: "In Treatment", intake: "08/15/2026", phone: "+1 (480) 555-0172", email: "iris.west@centralaz.com" },
    { id: "1031", firstName: "Elijah", lastName: "Wood", dob: "1981-12-18", illness: "Post-Stroke Rehab", status: "Active", intake: "08/12/2026", phone: "+1 (303) 555-0128", email: "e.wood@rockymountain.org" },
    { id: "1030", firstName: "Aurora", lastName: "Borealis", dob: "1997-06-25", illness: "Migraine Prophylaxis", status: "Active", intake: "08/10/2026", phone: "+1 (615) 555-0183", email: "aurora.b@tennesseehealth.net" },
    { id: "1029", firstName: "Silas", lastName: "Stone", dob: "1975-10-10", illness: "ALS Monitoring", status: "In Treatment", intake: "08/08/2026", phone: "+1 (202) 555-0149", email: "silas.stone@capitolmed.gov" },
    { id: "1028", firstName: "Gemma", lastName: "Arden", dob: "1992-01-14", illness: "Concussion Protocol", status: "Active", intake: "08/05/2026", phone: "+1 (415) 555-0190", email: "g.arden@bayareamed.org" },
    { id: "1027", firstName: "Felix", lastName: "Baumgartner", dob: "1984-08-30", illness: "Vertigo Evaluation", status: "Active", intake: "08/02/2026", phone: "+43 1 555 0163", email: "felix.b@viennahealth.at" },
    { id: "1026", firstName: "Daphne", lastName: "Moon", dob: "1990-05-11", illness: "Carpal Tunnel Syndrome", status: "Pending", intake: "07/30/2026", phone: "+1 (212) 555-0125", email: "daphne.moon@nycrehab.com" },
    { id: "1025", firstName: "Orion", lastName: "Black", dob: "1988-11-20", illness: "Sciatica Treatment", status: "Active", intake: "07/28/2026", phone: "+1 (617) 555-0177", email: "orion.black@bostonspine.net" },
    { id: "1024", firstName: "Lyra", lastName: "Belacqua", dob: "1996-07-03", illness: "EEG Diagnostic Scan", status: "Active", intake: "07/25/2026", phone: "+44 1865 555011", email: "lyra.b@oxfordmed.ac.uk" },
    { id: "1023", firstName: "Alexander", lastName: "Wright", dob: "1988-04-18", illness: "Migraine Evaluation", status: "Active", intake: "09/01/2026", phone: "+1 (415) 892-3401", email: "a.wright@healthmail.com" },
    { id: "1022", firstName: "Charlotte", lastName: "Bennett", dob: "1994-11-24", illness: "Routine Neurological", status: "Active", intake: "08/28/2026", phone: "+1 (212) 749-1120", email: "c.bennett@careconnect.io" },
    { id: "1021", firstName: "Lucas", lastName: "Miller", dob: "1990-07-15", illness: "Peripheral Neuropathy", status: "In Treatment", intake: "08/18/2026", phone: "+1 (617) 503-8842", email: "lucas.miller@neuroez.com" },
    { id: "1020", firstName: "Sophia", lastName: "Anderson", dob: "1985-09-03", illness: "Post-Concussion Check", status: "Active", intake: "08/11/2026", phone: "+1 (206) 438-9102", email: "sophia.anderson@medlink.org" },
    { id: "1019", firstName: "Oliver", lastName: "Davies", dob: "1992-12-12", illness: "Epilepsy Follow-up", status: "In Treatment", intake: "08/09/2026", phone: "+44 20 7946 0912", email: "o.davies@nhs-portal.co.uk" },
    { id: "1018", firstName: "Emma", lastName: "Taylor", dob: "1997-03-29", illness: "Chronic Fatigue", status: "Active", intake: "08/07/2026", phone: "+1 (312) 809-5431", email: "emma.taylor@globalcare.us" },
    { id: "1017", firstName: "Benjamin", lastName: "Hayes", dob: "1982-08-14", illness: "Cervical Radiculopathy", status: "In Treatment", intake: "08/02/2026", phone: "+1 (512) 674-2291", email: "b.hayes@austinhealth.com" },
    { id: "1016", firstName: "Isabella", lastName: "Martinez", dob: "1995-05-21", illness: "Clinical Consultation", status: "Active", intake: "07/16/2026", phone: "+1 (305) 912-7634", email: "i.martinez@floridaclinic.org" },
    { id: "1015", firstName: "Liam", lastName: "O'Connor", dob: "1989-10-05", illness: "Diagnostic MRI Review", status: "Active", intake: "07/15/2026", phone: "+353 1 496 0122", email: "liam.oconnor@dublincare.ie" },
    { id: "1014", firstName: "Amelia", lastName: "Wilson", dob: "1996-02-17", illness: "Sleep Apnea Screening", status: "Pending", intake: "07/12/2026", phone: "+1 (646) 381-0092", email: "a.wilson@manhattanmed.com" },
    { id: "1013", firstName: "Ethan", lastName: "Campbell", dob: "1991-06-30", illness: "Essential Tremor", status: "Active", intake: "07/10/2026", phone: "+1 (404) 521-8840", email: "ethan.campbell@atlantahealth.net" },
    { id: "1012", firstName: "Mia", lastName: "Johnson", dob: "1987-01-08", illness: "Sciatic Nerve Review", status: "In Treatment", intake: "07/08/2026", phone: "+1 (702) 834-5190", email: "mia.johnson@vegasmed.com" },
    { id: "1011", firstName: "Noah", lastName: "Smith", dob: "1993-09-19", illness: "Cluster Headache Protocol", status: "Active", intake: "07/05/2026", phone: "+1 (858) 490-1277", email: "noah.smith@pacificcare.org" },
    { id: "1010", firstName: "Harper", lastName: "Evans", dob: "1998-04-02", illness: "Motor Neuron Review", status: "Active", intake: "07/01/2026", phone: "+44 161 496 0834", email: "harper.evans@manchesterhealth.co.uk" },
    { id: "1009", firstName: "Mason", lastName: "Scott", dob: "1984-11-11", illness: "Post-Stroke Assessment", status: "In Treatment", intake: "06/28/2026", phone: "+1 (480) 612-4091", email: "m.scott@phoenixclinic.com" },
    { id: "1008", firstName: "Ava", lastName: "Robinson", dob: "1995-08-26", illness: "Vestibular Migraine", status: "Active", intake: "06/25/2026", phone: "+1 (303) 789-2210", email: "ava.robinson@denvermed.net" },
    { id: "1002", firstName: "Henry", lastName: "Brooks", dob: "1986-10-14", illness: "Spasticity Screening", status: "Active", intake: "06/22/2026", phone: "+1 (615) 430-8821", email: "h.brooks@nashvillehealth.org" },
    { id: "1001", firstName: "Ella", lastName: "Watson", dob: "1993-07-09", illness: "EEG Follow-up", status: "Active", intake: "06/20/2026", phone: "+1 (202) 555-0188", email: "ella.watson@capitalcare.com" },
  ]);

  const handleClear = () => {
    setFirstName("");
    setLastName("");
    setDob("");
    setAccountNo("");
    setInactive(false);
  };

  const handleDelete = (id) => {
    setPatientList((prev) => prev.filter((p) => p.id !== id));
  };

  const filteredPatients = patientList.filter((p) => {
    const matchesFirst = firstName ? p.firstName.toLowerCase().includes(firstName.toLowerCase()) : true;
    const matchesLast = lastName ? p.lastName.toLowerCase().includes(lastName.toLowerCase()) : true;
    const matchesDob = dob ? p.dob.includes(dob) : true;
    const matchesAcc = accountNo ? p.id.includes(accountNo) : true;
    return matchesFirst && matchesLast && matchesDob && matchesAcc;
  });

  return (
    <Box sx={{ width: "100%", pb: 5 }}>
      
      {/* Top Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.25rem",
            fontWeight: 800,
            color: primaryTextColor,
            letterSpacing: "-0.3px",
          }}
        >
          Patients
        </Typography>

        <Button
          variant="contained"
          startIcon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: "1.15rem !important" }} />}
          sx={{
            backgroundColor: tealMain,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.85rem",
            borderRadius: "8px",
            px: 2.8,
            py: 0.9,
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#164748",
            },
          }}
        >
          ADD
        </Button>
      </Box>

      {/* Form Filter Container */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "14px",
          border: `1.5px solid ${tealMain}`,
          p: { xs: 2.5, md: 3 },
          mb: 4,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.03)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2.2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              First Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& input": { py: "8.5px", px: "12px" },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              Last Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& input": { py: "8.5px", px: "12px" },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              DOB
            </Typography>
            <TextField
              fullWidth
              type="date"
              size="small"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& input": { py: "8.5px", px: "12px" },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: labelTeal, mb: 0.6 }}>
              Account No
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Account No"
              value={accountNo}
              onChange={(e) => setAccountNo(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                  fontSize: "0.82rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: "#CBD5E1" },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
                "& input": { py: "8.5px", px: "12px" },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={inactive}
                onChange={(e) => setInactive(e.target.checked)}
                size="small"
                sx={{
                  color: "#94A3B8",
                  "&.Mui-checked": { color: tealMain },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#475569" }}>
                Inactive
              </Typography>
            }
            sx={{ mr: "auto" }}
          />

          <Button
            variant="contained"
            startIcon={<ArrowUpwardRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              backgroundColor: importGreen,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "8px",
              px: 2.6,
              py: 0.85,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#046337" },
            }}
          >
            IMPORT
          </Button>

          <Button
            variant="contained"
            startIcon={<SearchIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "8px",
              px: 2.6,
              py: 0.85,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1C4E54" },
            }}
          >
            SEARCH
          </Button>

          <Button
            variant="contained"
            startIcon={<CleaningServicesOutlinedIcon sx={{ fontSize: "1.1rem !important" }} />}
            onClick={handleClear}
            sx={{
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "8px",
              px: 2.6,
              py: 0.85,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1C4E54" },
            }}
          >
            CLEAR
          </Button>
        </Box>
      </Box>

      {/* Patient Data Table with Clean Vertical Scrollbar */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "14px",
          border: "1px solid #EAEFF4",
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.03)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            maxHeight: "520px",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#F8FAFC" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#CBD5E1",
              borderRadius: "10px",
              "&:hover": { backgroundColor: tealMain },
            },
          }}
        >
          <Table stickyHeader sx={{ width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: secondaryTextColor,
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    textTransform: "uppercase",
                    backgroundColor: isDark ? "#162238" : "#F8FAFC",
                    borderBottom: "1px solid #EAEFF4",
                    py: 1.6,
                  },
                }}
              >
                <TableCell>Account</TableCell>
                <TableCell>Patient Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>DOB</TableCell>
                <TableCell>Clinical Condition</TableCell>
                <TableCell>Intake</TableCell>
                <TableCell>Contact Details</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredPatients
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: isDark ? "rgba(56, 189, 248, 0.04)" : "#F8FAFC" },
                      "& td": {
                        borderBottom: "1px solid #F1F5F9",
                        py: 1.4,
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: tealButton,
                          px: 1,
                          py: 0.3,
                          borderRadius: "5px",
                          backgroundColor: "#D9EBEF",
                        }}
                      >
                        #{row.id}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: "0.74rem",
                            fontWeight: 800,
                            backgroundColor: "#D9EBEF",
                            color: tealButton,
                          }}
                        >
                          {row.firstName[0]}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: primaryTextColor, lineHeight: 1.2 }}>
                            {row.firstName} {row.lastName}
                          </Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: secondaryTextColor, fontWeight: 500 }}>
                            {row.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.6,
                          px: 1,
                          py: 0.25,
                          borderRadius: "20px",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          backgroundColor:
                            row.status === "Active"
                              ? isDark ? "rgba(52, 211, 153, 0.15)" : "#ECFDF5"
                              : isDark ? "rgba(251, 146, 60, 0.15)" : "#FFF7ED",
                          color: row.status === "Active" ? "#10B981" : "#EA580C",
                        }}
                      >
                        <FiberManualRecordIcon sx={{ fontSize: "0.45rem" }} />
                        {row.status}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.82rem", color: secondaryTextColor, fontWeight: 500 }}>
                      {row.dob}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.illness}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          borderRadius: "5px",
                          backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
                          color: primaryTextColor,
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.82rem", color: secondaryTextColor, fontWeight: 500 }}>
                      {row.intake}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.82rem", color: primaryTextColor, fontWeight: 600 }}>
                      {row.phone}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                        <Tooltip title="View Medical Chart">
                          <IconButton size="small" sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}>
                            <VisibilityOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Demographics">
                          <IconButton size="small" sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}>
                            <EditOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Archive / Delete">
                          <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: "#EF4444", "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" } }}>
                            <DeleteOutlineIcon sx={{ fontSize: "1.05rem" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 25]}
          component="div"
          count={filteredPatients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: "1px solid #EAEFF4", color: secondaryTextColor }}
        />
      </Box>

    </Box>
  );
};

export default Patients;